/* WhatsApp helper — `wa.me` link builder with phone-quality validation.
 *
 * Why this exists: `wa.me/<digits>?text=<msg>` opens WhatsApp Web/app
 * even when the destination phone is bogus (placeholder, missing
 * country code, obviously fake). Doctors then think they sent a nudge
 * but the message never reaches the patient.
 *
 * Rules a phone must pass to get a wa.me link:
 *   • >= 10 digits after stripping non-numerics (E.164-ish minimum)
 *   • not on the seed-data PLACEHOLDER list (Riya's clinic seed uses
 *     +918000000001 / +918000000002 etc. on lite-patients)
 *   • not a "fake-pattern" number — all same digit, sequential 01234…
 *
 * Returns either a `wa.me` URL or null. Callers should hide / disable
 * the trigger when null and surface a helpful tooltip via `waReason`.
 */
const PLACEHOLDER_NUMBERS = new Set([
    // Soft-launch seed lite-patient phones — these get reused across many
    // patients so the WhatsApp message would deliver to the wrong inbox.
    "918000000001", "918000000002", "918000000003",
    "918000099091",
]);


function _digits(phone) {
    return String(phone || "").replace(/\D/g, "");
}


function _isFakePattern(d) {
    if (!d) return true;
    if (/^(\d)\1+$/.test(d)) return true;                    // all same digit
    if (d === "01234567890" || d === "12345678901") return true;
    return false;
}


/** Reason a phone is unusable, in human language. Null when phone is OK. */
export function waReason(phone) {
    const d = _digits(phone);
    if (!d) return "No phone number on file";
    if (d.length < 10) return "Phone number is too short to send WhatsApp";
    if (PLACEHOLDER_NUMBERS.has(d)) return "Placeholder phone — update on patient profile";
    if (_isFakePattern(d)) return "Phone number looks invalid";
    return null;
}


/** Returns a usable `wa.me` URL for the phone + message, or null when
    the phone fails validation. */
export function waLink(phone, message) {
    if (waReason(phone)) return null;
    const d = _digits(phone);
    return `https://wa.me/${d}?text=${encodeURIComponent(message || "")}`;
}


/** Same idea but for the marketing/public team's own number (Invisique's
    own WhatsApp). Skips placeholder/format checks since this is a known
    business number stored in the env var. */
export function waLinkBusiness(businessPhone, message) {
    const d = _digits(businessPhone);
    if (!d || d.length < 10) return null;
    return `https://wa.me/${d}?text=${encodeURIComponent(message || "")}`;
}


/* Page-aware WhatsApp prefill copy.
 *
 * Why: a generic "Hi Invisique!" opens the inbox with zero context, so
 * the sales/care team has to ask "what page were you on?" before they
 * can help. Pre-baking the page into the message means the first reply
 * is already on-point. Marketing-side surfaces (the FAB) only — patient
 * portal & doctor portal use their own purpose-built chat threads.
 *
 * Pathname is matched longest-prefix first; unknown routes fall back to
 * the generic home greeting. Keep entries scoped to public marketing
 * pages (anything `SiteShell`-wrapped in App.js). */
const _PAGE_PREFILLS = [
    ["/why-invisique", "Hi Invisique! I was reading 'Why Invisique' and have a question — can you help?"],
    ["/how-it-works", "Hi Invisique! I just read 'How It Works' and have a question about the treatment process."],
    ["/find-doctor", "Hi Invisique! I'm looking for a certified Invisique™ doctor near me — can you help?"],
    ["/pricing", "Hi Invisique! I have a question about pricing and plans for Invisique™ clear aligners."],
    ["/shop", "Hi Invisique! I have a question about the Invisique™ shop / products."],
    ["/infinite", "Hi Invisique! I'd like to know more about Invisique™ Infinite Retainers."],
    ["/smile-assessment", "Hi Invisique! I'm taking the Smile Assessment and have a quick question."],
    ["/smile-simulator", "Hi Invisique! I tried the AI Smile Simulator — can we discuss next steps?"],
    ["/contact", "Hi Invisique! I'd like to get in touch — saw your contact page."],
    ["/for-doctors", "Hi Invisique! I'm a dental professional interested in partnering with Invisique."],
    ["/for-providers", "Hi Invisique! I'm a dental professional interested in partnering with Invisique."],
    ["/become-a-provider", "Hi Invisique! I'm a dental professional interested in partnering with Invisique."],
    ["/faq", "Hi Invisique! I was reading your FAQs and still have a question."],
    ["/book", "Hi Invisique! I'm trying to book a consultation and need a quick hand."],
];

const _DEFAULT_PREFILL = "Hi Invisique! I'd like to know more about clear aligners.";

export function pageContextMessage(pathname) {
    if (!pathname || typeof pathname !== "string") return _DEFAULT_PREFILL;
    for (const [prefix, msg] of _PAGE_PREFILLS) {
        if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return msg;
    }
    return _DEFAULT_PREFILL;
}
